import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import InvoiceCard from './InvoiceCard';
import { formatDatePSTShort } from '../../utils/dateUtils';
import './BillingDashboard.css';

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total: number;
  balance_due: number;
  status: string;
  student_id: string;
  student_name?: string;
  parent_name?: string;
}

interface InvoiceListProps {
  studentId?: string;
  parentId?: string;
  onInvoiceClick?: (invoiceId: string) => void;
  onPayInvoice?: (invoiceId: string) => void;
  onViewInvoice?: (invoiceId: string) => void;
  onDownloadInvoice?: (invoiceId: string) => void;
  onDeleteInvoice?: (invoiceId: string) => void;
  onStatusChange?: (invoiceId: string, newStatus: string) => void;
  isAdmin?: boolean;
  refreshTrigger?: number; // When this changes, refresh the invoice list
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  studentId,
  parentId,
  onInvoiceClick,
  onPayInvoice,
  onViewInvoice,
  onDownloadInvoice,
  onDeleteInvoice,
  onStatusChange,
  isAdmin,
  refreshTrigger
}) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'status'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchInvoices();
  }, [studentId, parentId, refreshTrigger]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let url = '';
      if (studentId) {
        url = `${config.BACKEND_URL}/api/billing/invoices/student/${studentId}`;
      } else if (parentId) {
        url = `${config.BACKEND_URL}/api/billing/invoices/parent/${parentId}`;
      } else {
        // Admin view - get all invoices
        url = `${config.BACKEND_URL}/api/billing/invoices`;
      }
      
      const response = await axios.get(url, { headers });
      setInvoices(response.data || []);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (invoiceId: string) => {
    if (onViewInvoice) {
      onViewInvoice(invoiceId);
    } else if (onInvoiceClick) {
      onInvoiceClick(invoiceId);
    }
  };

  const handlePay = (invoiceId: string) => {
    if (onPayInvoice) {
      onPayInvoice(invoiceId);
    }
  };

  const handleDownload = (invoiceId: string) => {
    if (onDownloadInvoice) {
      onDownloadInvoice(invoiceId);
    }
  };

  const handleDelete = (invoiceId: string) => {
    if (onDeleteInvoice) {
      onDeleteInvoice(invoiceId);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filters.status !== 'all' && invoice.status !== filters.status) {
      return false;
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!invoice.invoice_number.toLowerCase().includes(searchLower) &&
          !invoice.student_name?.toLowerCase().includes(searchLower) &&
          !invoice.parent_name?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    if (filters.dateFrom && invoice.invoice_date < filters.dateFrom) {
      return false;
    }
    
    if (filters.dateTo && invoice.invoice_date > filters.dateTo) {
      return false;
    }
    
    return true;
  });

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'date':
        comparison = new Date(a.invoice_date).getTime() - new Date(b.invoice_date).getTime();
        break;
      case 'amount':
        comparison = a.total - b.total;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="invoice-list-container">
      <div className="invoice-list-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Invoice number, student, parent..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>From Date:</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>To Date:</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Sort By:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'amount' | 'status')}
            className="filter-select"
          >
            <option value="date">Date</option>
            <option value="amount">Amount</option>
            <option value="status">Status</option>
          </select>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>
      
      <div className="invoice-list-stats">
        <span>Showing {sortedInvoices.length} of {invoices.length} invoices</span>
      </div>
      
      {sortedInvoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices found</p>
        </div>
      ) : (
        <div className="invoice-list-grid">
          {sortedInvoices.map(invoice => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              onView={handleView}
              onPay={onPayInvoice}
              onDownload={onDownloadInvoice}
              onDelete={handleDelete}
              onStatusChange={onStatusChange}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InvoiceList;

